import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Users, Plus, Shield, UserCheck, Edit2, Eye, EyeOff, Copy, Trash2, Loader2, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { getUsers, createUser, updateUser, deleteUser, getEmployees } from "../services/api";
import { TABS, ROLE_LABELS, ROLE_COLORS } from "../utils/tabs";

const ALL_TAB_KEYS = TABS.map((t) => t.key);

const emptyForm = { email: "", name: "", phone: "", password: "", role: "employe", allowed_tabs: [...ALL_TAB_KEYS], employee_id: "none", can_manage_events: false, can_view_equipment_prices: false };

const TabsPicker = ({ value, onChange, disabled }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <Label className="text-slate-600 text-xs uppercase tracking-wider flex items-center gap-1.5">
        <LayoutGrid size={12} /> Onglets accessibles
      </Label>
      {!disabled && (
        <div className="flex gap-2 text-[11px]">
          <button className="text-[#26788A] hover:underline" onClick={() => onChange([...ALL_TAB_KEYS])} data-testid="tabs-select-all">Tout cocher</button>
          <button className="text-slate-400 hover:underline" onClick={() => onChange([])} data-testid="tabs-select-none">Tout décocher</button>
        </div>
      )}
    </div>
    {disabled ? (
      <p className="text-xs text-slate-400 border border-slate-200 rounded-lg p-3">L'administrateur a accès à tous les onglets.</p>
    ) : (
      <div className="grid grid-cols-2 gap-1.5 border border-slate-200 rounded-lg p-2.5">
        {TABS.map((t) => {
          const checked = value.includes(t.key);
          return (
            <label key={t.key} className={`flex min-w-0 min-h-11 items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${checked ? "bg-teal-50" : "hover:bg-slate-50"}`} data-testid={`tab-check-${t.key.replace(/_/g, "-")}`}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onChange(checked ? value.filter((k) => k !== t.key) : [...value, t.key])}
                className="accent-[#26788A] w-3.5 h-3.5 shrink-0"
                data-testid={`tab-checkbox-${t.key.replace(/_/g, "-")}`}
              />
              <t.icon size={13} className="text-slate-400 shrink-0" />
              <span className="text-xs text-slate-700">{t.label}</span>
            </label>
          );
        })}
      </div>
    )}
  </div>
);

const UserForm = ({ form, setForm, isEdit, employees }) => (
  <div className="space-y-4 py-2">
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-slate-600 text-xs uppercase tracking-wider">Nom</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 input-field" placeholder="Prénom Nom" data-testid="user-form-name" />
      </div>
      <div>
        <Label className="text-slate-600 text-xs uppercase tracking-wider">Téléphone</Label>
        <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 input-field" placeholder="06 12 34 56 78" data-testid="user-form-phone" />
      </div>
    </div>
    <div>
      <Label className="text-slate-600 text-xs uppercase tracking-wider">Email (identifiant)</Label>
      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 input-field" placeholder="email@studioraneem.fr" data-testid="user-form-email" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-slate-600 text-xs uppercase tracking-wider">Mot de passe {isEdit && <span className="normal-case text-slate-400">(laisser vide = inchangé)</span>}</Label>
        <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 input-field font-mono" placeholder={isEdit ? "••••••" : "6 caractères min."} data-testid="user-form-password" />
      </div>
      <div>
        <Label className="text-slate-600 text-xs uppercase tracking-wider">Rôle</Label>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger className="mt-1.5 input-field" data-testid="user-form-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
    <div>
      <Label className="text-slate-600 text-xs uppercase tracking-wider">Fiche employé liée (pour "Mon planning")</Label>
      <Select value={form.employee_id || "none"} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
        <SelectTrigger className="mt-1.5 input-field" data-testid="user-form-employee"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Aucune (détection automatique par email)</SelectItem>
          {(employees || []).map((e) => (
            <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    {form.role !== "admin" && (
      <>
      <label className="flex items-center gap-2.5 border border-slate-200 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors" data-testid="user-form-can-manage-events">
        <input
          type="checkbox"
          checked={!!form.can_manage_events}
          onChange={(e) => setForm({ ...form, can_manage_events: e.target.checked })}
          className="accent-[#26788A] w-4 h-4"
          data-testid="can-manage-events-checkbox"
        />
        <span className="text-sm text-slate-700">Peut créer des tâches / événements / tournages et les attribuer aux autres</span>
      </label>
      <label className="flex items-center gap-2.5 border border-slate-200 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors" data-testid="user-form-can-view-prices">
        <input
          type="checkbox"
          checked={!!form.can_view_equipment_prices}
          onChange={(e) => setForm({ ...form, can_view_equipment_prices: e.target.checked })}
          className="accent-[#26788A] w-4 h-4"
          data-testid="can-view-prices-checkbox"
        />
        <span className="text-sm text-slate-700">Accès aux tarifs du matériel (voir / modifier valeur d'achat et tarif location)</span>
      </label>
      </>
    )}
    <TabsPicker
      value={form.allowed_tabs || []}
      onChange={(tabs) => setForm({ ...form, allowed_tabs: tabs })}
      disabled={form.role === "admin"}
    />
  </div>
);

const UsersPage = () => {
  const { user: currentUser, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [visiblePwd, setVisiblePwd] = useState({});

  useEffect(() => {
    if (hasPermission("manage_users")) loadUsers();
  }, [hasPermission]);

  const loadUsers = async () => {
    try {
      const [res, empRes] = await Promise.all([getUsers(), getEmployees().catch(() => ({ data: [] }))]);
      setUsers(res.data);
      setEmployees((empRes.data || []).filter((e) => e.is_active !== false));
    } catch {
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setForm(emptyForm); setShowCreate(true); };
  const openEdit = (u) => {
    setForm({
      email: u.email, name: u.name, phone: u.phone || "", password: "",
      role: u.role, allowed_tabs: u.allowed_tabs || [...ALL_TAB_KEYS],
      employee_id: u.employee_id || "none", can_manage_events: !!u.can_manage_events,
      can_view_equipment_prices: !!u.can_view_equipment_prices,
    });
    setEditUser(u);
  };

  const handleCreate = async () => {
    if (!form.email || !form.name) { toast.error("Nom et email requis"); return; }
    if (!form.password || form.password.length < 6) { toast.error("Mot de passe de 6 caractères minimum requis"); return; }
    try {
      setSaving(true);
      await createUser({
        email: form.email, name: form.name, phone: form.phone || null,
        password: form.password, role: form.role,
        allowed_tabs: form.role === "admin" ? null : form.allowed_tabs,
        employee_id: form.employee_id === "none" ? null : form.employee_id,
        can_manage_events: form.role === "admin" ? false : form.can_manage_events,
        can_view_equipment_prices: form.role === "admin" ? false : form.can_view_equipment_prices,
      });
      setShowCreate(false);
      loadUsers();
      toast.success("Utilisateur créé avec succès");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    if (form.password && form.password.length < 6) { toast.error("Le mot de passe doit contenir au moins 6 caractères"); return; }
    try {
      setSaving(true);
      const payload = {
        name: form.name, email: form.email, phone: form.phone || null,
        role: form.role, allowed_tabs: form.role === "admin" ? null : form.allowed_tabs,
        employee_id: form.employee_id === "none" ? null : form.employee_id,
        can_manage_events: form.role === "admin" ? false : form.can_manage_events,
        can_view_equipment_prices: form.role === "admin" ? false : form.can_view_equipment_prices,
      };
      if (form.password) payload.password = form.password;
      await updateUser(editUser.id, payload);
      setEditUser(null);
      loadUsers();
      toast.success("Utilisateur modifié");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${u.name} (${u.email}) ?\n\nCette action est irréversible.`)) return;
    try {
      await deleteUser(u.id);
      loadUsers();
      toast.success("Utilisateur supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papiers");
  };

  if (!hasPermission("manage_users")) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-slate-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Accès refusé</h2>
          <p className="text-slate-500 mt-2">Vous n'avez pas la permission d'accéder à cette page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 size={24} className="animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fadeIn max-w-5xl mx-auto overflow-y-auto h-screen" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
            <Users size={28} className="text-[#26788A]" />
            Utilisateurs
          </h1>
          <p className="text-slate-500 mt-1">Comptes, rôles, mots de passe et accès aux onglets</p>
        </div>
        <Button onClick={openCreate} className="btn-primary" data-testid="create-user-btn">
          <Plus size={16} className="mr-2" />
          Nouvel utilisateur
        </Button>
      </div>

      {/* Users List */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {users.map((u) => {
              const shown = visiblePwd[u.id];
              const tabsCount = u.role === "admin" ? TABS.length : (u.allowed_tabs ? u.allowed_tabs.length : TABS.length);
              return (
                <div key={u.id} className={`px-5 py-4 ${!u.is_active ? "bg-slate-50 opacity-60" : ""}`} data-testid={`user-item-${u.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white shrink-0 ${u.is_active ? "bg-[#26788A]" : "bg-slate-400"}`}>
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{u.name}</span>
                        {u.id === currentUser.id && <Badge className="bg-[#26788A] text-white text-xs">Vous</Badge>}
                        {!u.is_active && <Badge className="bg-slate-200 text-slate-600 text-xs">Désactivé</Badge>}
                        <Badge className={`${ROLE_COLORS[u.role] || "bg-slate-100 text-slate-700"} text-xs font-medium`}>{ROLE_LABELS[u.role] || u.role}</Badge>
                      </div>
                      <p className="text-sm text-slate-500">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{tabsCount}/{TABS.length} onglets accessibles</p>
                    </div>

                    {/* Mot de passe visible (admin) */}
                    <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid={`user-password-${u.id}`}>
                      <span className="font-mono text-sm text-slate-700 min-w-[90px]">
                        {u.password_plain ? (shown ? u.password_plain : "••••••••") : <span className="text-slate-400 text-xs italic">non connu</span>}
                      </span>
                      {u.password_plain && (
                        <>
                          <button className="text-slate-400 hover:text-slate-700" onClick={() => setVisiblePwd({ ...visiblePwd, [u.id]: !shown })} data-testid={`toggle-password-${u.id}`}>
                            {shown ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button className="text-slate-400 hover:text-slate-700" onClick={() => copy(u.password_plain)} data-testid={`copy-password-${u.id}`}>
                            <Copy size={13} />
                          </button>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-slate-600 hover:text-slate-900" data-testid={`edit-user-${u.id}`}>
                        <Edit2 size={16} />
                      </Button>
                      {u.id !== currentUser.id && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(u)} className="text-slate-600 hover:text-red-500" data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Légende rôles */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <div key={value}>
                <Badge className={`${ROLE_COLORS[value]} mb-2`}>{label}</Badge>
                <p className="text-xs text-slate-500">
                  {value === "admin" ? "Accès total + gestion des utilisateurs" : "Accès selon les onglets cochés"}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="responsive-dialog bg-white max-w-lg max-h-[90dvh] overflow-y-auto" aria-describedby={undefined} data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck size={20} className="text-[#26788A]" />
              Nouvel utilisateur
            </DialogTitle>
          </DialogHeader>
          <UserForm form={form} setForm={setForm} isEdit={false} employees={employees} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="btn-outline">Annuler</Button>
            <Button onClick={handleCreate} className="btn-primary" disabled={saving} data-testid="submit-create-user">
              {saving && <Loader2 size={14} className="animate-spin mr-1" />} Créer l'utilisateur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <DialogContent className="responsive-dialog bg-white max-w-lg max-h-[90dvh] overflow-y-auto" aria-describedby={undefined} data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 size={20} className="text-[#26788A]" />
              Modifier l'utilisateur
            </DialogTitle>
          </DialogHeader>
          <UserForm form={form} setForm={setForm} isEdit={true} employees={employees} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="btn-outline">Annuler</Button>
            <Button onClick={handleUpdate} className="btn-primary" disabled={saving} data-testid="submit-edit-user">
              {saving && <Loader2 size={14} className="animate-spin mr-1" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
